import React, { useEffect, useMemo, useRef, useState } from 'react';
import Menu from 'studio/components/Menu/Menu';
import { DbtIcon, TenantsIcon, VeezooAdminIcon, VeezooStudioIcon } from 'components/Icons';
import { Redirect, Route, Switch, useHistory, useLocation, useRouteMatch } from 'react-router-dom';
import Configuration from 'studio/pages/Configuration/Configuration';
import Import from 'studio/pages/Import/Import';
import DbtSync from 'studio/pages/DbtSync/DbtSync';
import StorageIcon from '@mui/icons-material/Storage';
import ManagedSearchIcon from '@mui/icons-material/ManageSearchRounded';
import BuildIcon from '@mui/icons-material/Build';
import KnowledgeGraphSwitcher from 'studio/components/KnowledgeGraphSwitcher/KnowledgeGraphSwitcher';
import BranchSwitcher from 'studio/components/BranchSwitcher/BranchSwitcher';
import { createTheme, ThemeProvider } from '@mui/material/styles';

import ResizableDrawer, {
  DefaultSidebarWidth,
  LocalStorageStudioSidebarWidthKey
} from 'studio/components/ResizableDrawer/ResizableDrawer';
import services from 'services';
import AppBar from 'studio/components/AppBar/AppBar';
import NewKnowledgeGraph from 'studio/pages/NewKnowledgeGraph/NewKnowledgeGraph';
import { darkTheme, lightTheme } from 'studio/themes';
import Progress from 'studio/components/Progress';
import Home from 'studio/pages/Home/Home';
import CircularProgress from '@mui/material/CircularProgress';
import Tooltip from '@mui/material/Tooltip';
import { trackEvent } from 'utils/eventTracking';
import { paths as adminPaths } from 'studio/App/Admin/Admin';
import Editor from 'studio/pages/Editor/Editor';
import IconButton from '@mui/material/IconButton';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import SyncIcon from '@mui/icons-material/Sync';
import SearchIcon from '@mui/icons-material/Search';
import Link from 'studio/components/Link';
import Synchronization from 'studio/pages/Synchronization/Synchronization';
import { ConfirmationModalProvider } from 'studio/components/ConfirmationModal/ConfirmationModal';
import { KgPermissions } from 'studio/constants';
import TenantAccess from 'studio/pages/TenantAccess/TenantAccess';
import _ from 'lodash';
import useLocalStorageState from 'use-local-storage-state';
import QueryEditor from 'studio/pages/QueryEditor/QueryEditor';
import ContextMenuContextProvider from 'components/ContextMenu/ContextMenu';
import CommandPalette from 'studio/components/CommandPalette/CommandPalette';
import { useEditorOpen } from 'studio/hooks/useEditorOpen';
import { loadingSx, useStyles } from './Studio.styles';

export const paths = {
  root: '/studio',
  configuration: '/studio/configuration',
  configurationFivetran: '/studio/configuration/:kgId',
  synchronization: '/studio/synchronization',
  dbtCloudSync: '/studio/dbt',
  tenants: '/studio/tenants',
  editor: '/studio/editor',
  import: '/studio/import',
  new: '/studio/new',
  queryEditor: '/studio/query-editor'
};

const Studio = ({ isDarkTheme, toggleTheme, user, languages, children }) => {
  useEffect(() => {
    trackEvent('Studio Opened');
  }, []);

  const [sidebarWidth] = useLocalStorageState(LocalStorageStudioSidebarWidthKey, {
    defaultValue: DefaultSidebarWidth,
    storageSync: false
  });
  const location = useLocation();
  const history = useHistory();
  const [isCommandPaletteOpen, setIsCommandPaletteOpen] = useState(false);

  const primary = '#7c3ad5';
  const themeProps = { primary, width: sidebarWidth };
  const theme = createTheme(isDarkTheme ? darkTheme(themeProps) : lightTheme(themeProps));

  const { classes } = useStyles();

  // the knowledge graphs (not including any tenant knowledge graphs)
  const [knowledgeGraphs, setKnowledgeGraphs] = useState(null);
  // the currently selected knowledge graph
  const [knowledgeGraph, setKnowledgeGraph] = useState(null);

  // Contains organization settings
  const [orgSettings, setOrgSettings] = useState(null);

  // contains flags, indexed by knowledge graph ID, specifying whether a given knowledge graph has a running background task
  const [runningBackgroundTasks, setRunningBackgroundTasks] = useState({});

  const kgPermissions = useMemo(() => knowledgeGraph?.data.kgPermissions || [], [knowledgeGraph]);
  // reference to the container for the Editor menu
  const editorMenuContainerRef = useRef(null);
  // the reference to the Editor component
  const editorRef = useRef(null);
  // specifies whether we show or hide the Editor given the current route
  const isAllowedToSeeEditor = useMemo(() => kgPermissions.includes(KgPermissions.KgVklRead), [kgPermissions]);
  const showEditor = !!useRouteMatch({ path: paths.editor, exact: true }) && isAllowedToSeeEditor;

  const isLoading = !user || !languages || !knowledgeGraphs;

  // sets the knowledge graph based on the 'id' URL parameter
  useEffect(() => {
    if (knowledgeGraphs) {
      const params = new URLSearchParams(location.search);
      const id = params.get('id');
      // get the knowledge graph specified by the URL parameter
      const knowledgeGraph = knowledgeGraphs.find(knowledgeGraph => knowledgeGraph.data.id === id);
      // if no knowledge graph matched the ID, then delete the URL parameter
      if (id && !knowledgeGraph) {
        params.delete('id');
        history.replace({
          pathname: location.pathname,
          search: '?' + params.toString(),
          hash: location.hash
        });
      }
      // set the knowledge graph
      setKnowledgeGraph(knowledgeGraph);
    }
  }, [knowledgeGraphs, knowledgeGraphs, location]);

  const getOrgSettings = async () => {
    try {
      const response = await services.getSettings();
      if (response.success) {
        setOrgSettings(response.data);
      } else {
        console.error('Error fetching organization settings');
      }
    } catch (error) {
      console.error('Error fetching organization settings:', error);
    }
  };

  const getKnowledgeGraphs = async () => {
    let knowledgeGraphs = null;

    const response = await services.getKnowledgeGraphs();

    if (response.success) {
      knowledgeGraphs = response.data.data;
      setKnowledgeGraphs(knowledgeGraphs);
    }

    return knowledgeGraphs;
  };

  useEffect(() => {
    getKnowledgeGraphs();
    getOrgSettings();
  }, []);

  const kgId = knowledgeGraph?.data?.id;

  // Filter out dev branches for KG switcher - similar to Chat UI logic
  const mainKnowledgeGraphs = useMemo(() => {
    if (!knowledgeGraphs) return [];
    return knowledgeGraphs.filter(kg => !kg.data.developmentKgForName);
  }, [knowledgeGraphs]);

  // For KG Switcher display, if we're on a dev branch, show the main KG instead
  const displayKnowledgeGraph = useMemo(() => {
    if (!knowledgeGraph?.data) return knowledgeGraph;

    const developmentKgForName = knowledgeGraph.data.developmentKgForName;
    if (developmentKgForName) {
      // Find the main KG that this dev branch is based on
      const mainKg = knowledgeGraphs?.find(kg => kg.data.info.default.name === developmentKgForName);
      return mainKg || knowledgeGraph;
    }
    return knowledgeGraph;
  }, [knowledgeGraph, knowledgeGraphs]);

  // Handle keyboard shortcut: open command palette on Ctrl+K (Windows) or Cmd+K (Mac)
  useEffect(() => {
    function handleKeyDown(e) {
      const isWinCtrl = e.ctrlKey && e.key === 'k';
      const isMacCmd = e.metaKey && e.key === 'k';
      if ((isWinCtrl || isMacCmd) && !e.shiftKey && !e.altKey && kgId) {
        e.preventDefault();
        setIsCommandPaletteOpen(true);
      }
    }

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [kgId]);

  const searchFilesAndConcepts = async query => {
    try {
      if (!kgId) return [];
      const response = await services.searchFilesAndConcepts(kgId, query);
      if (response.success) {
        return response.data.results.map(result => ({
          id: result.vklId || result.filePath,
          label: result.vklId || result.filePath,
          data: result
        }));
      }
      return [];
    } catch (error) {
      console.error('Search error:', error);
      return [];
    }
  };

  const openInEditor = useEditorOpen();

  const jumpToSearchResult = ({ data }) => {
    if (data.vklId) {
      openInEditor(data.filePath, data.vklId);
    } else {
      openInEditor(data.filePath);
    }
    setIsCommandPaletteOpen(false);
  };

  const setRunningBackgroundTask = (knowledgeGraph, value) => {
    setRunningBackgroundTasks(tasks => ({ ...tasks, [knowledgeGraph.data.id]: value }));
  };

  // Returns a promise that can be waited on
  // Check the API endpoint for the given KG, and then set the status correspondingly
  const checkIfBackgroundTaskIsRunning = knowledgeGraph => {
    if (knowledgeGraph) {
      const kgId = knowledgeGraph.data.id;
      return services.isBackgroundSyncRunning(kgId).then(response => {
        setRunningBackgroundTask(knowledgeGraph, response.data);
      });
    } else {
      return Promise.resolve(null);
    }
  };

  // Check background tasks every time the KG is switched
  useEffect(() => {
    if (knowledgeGraph) {
      checkIfBackgroundTaskIsRunning(knowledgeGraph);
    }
  }, [knowledgeGraph, location.pathname]);

  // Periodically check whether background task is still running
  useEffect(() => {
    // The block of code ensures that we only query the backend once the previous request completed, and that the timeout is properly taken care of
    if (knowledgeGraph && runningBackgroundTasks[knowledgeGraph.data.id]) {
      // Only check if background task is running
      const timeoutState = { shutdown: false, timeout: undefined }; // Needed to avoid issues with closures
      const repeatingFunc = () => {
        timeoutState.timeout = undefined; // The timeout has been executed, no need to clean up
        checkIfBackgroundTaskIsRunning(knowledgeGraph).finally(() => {
          // Check if cleanup has been performed
          if (!timeoutState.shutdown) {
            timeoutState.timeout = setTimeout(repeatingFunc, 5000);
          }
        });
      };
      repeatingFunc();
      return () => {
        timeoutState.shutdown = true; // Force any running check to not spawn a new one
        if (timeoutState.timeout) {
          clearTimeout(timeoutState.timeout); // Clear the next check
        }
      };
    }
  }, [runningBackgroundTasks[knowledgeGraph?.data.id], knowledgeGraph?.data.id]);

  const content = (
    <>
      <AppBar
        title="Studio"
        icon={VeezooStudioIcon}
        href={paths.root}
        isDarkTheme={isDarkTheme}
        toggleTheme={toggleTheme}
        isSingleSignOn={user?.isSingleSignOn}
        knowledgeGraph={knowledgeGraph}
        user={user}
        additionalButton={{
          title: 'Open Veezoo Admin',
          icon: <VeezooAdminIcon />,
          href: adminPaths.root
        }}
      >
        {knowledgeGraph && runningBackgroundTasks[knowledgeGraph.data.id] && (
          <Tooltip
            title="A Background task is running, performance may be impacted until it is done."
            placement="bottom"
          >
            <CircularProgress sx={loadingSx} size={20} />
          </Tooltip>
        )}
        <Tooltip
          title={kgId ? 'Search files and concepts (Ctrl+K)' : 'Select a knowledge graph to search'}
          placement="bottom"
        >
          <span>
            <button
              onClick={() => kgId && setIsCommandPaletteOpen(true)}
              data-test="openCommandPaletteButton"
              disabled={!kgId}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '6px 12px',
                border: '1px solid rgba(255, 255, 255, 0.3)',
                borderRadius: '4px',
                background: 'rgba(255, 255, 255, 0.05)',
                color: 'inherit',
                cursor: kgId ? 'pointer' : 'default',
                opacity: kgId ? 1 : 0.5,
                fontSize: '14px',
                minWidth: '160px'
              }}
            >
              <SearchIcon fontSize="small" />
              <span style={{ flex: 1, textAlign: 'left', opacity: 0.7 }}>Search</span>
              <span style={{ opacity: 0.7, fontSize: '12px' }}>Ctrl+K</span>
            </button>
          </span>
        </Tooltip>
        <Tooltip title="Create a new Knowledge Graph" placement="bottom">
          <IconButton
            color="inherit"
            size="large"
            component={Link}
            href={paths.new}
            knowledgeGraph={knowledgeGraph}
            data-test="newKnowledgeGraphButton"
          >
            <AddCircleIcon />
          </IconButton>
        </Tooltip>
      </AppBar>
      <ResizableDrawer isResizable={true}>
        <KnowledgeGraphSwitcher
          className={classes.knowledgeGraphSwitcher}
          knowledgeGraphs={mainKnowledgeGraphs}
          knowledgeGraph={displayKnowledgeGraph}
        />
        {!!knowledgeGraph && (
          <BranchSwitcher
            knowledgeGraphs={knowledgeGraphs}
            knowledgeGraph={knowledgeGraph}
            user={user}
            fetchKnowledgeGraphs={getKnowledgeGraphs}
          />
        )}
        {!!knowledgeGraph && (
          <>
            <Menu
              className={classes.menu}
              items={_.compact([
                {
                  href: paths.configuration,
                  knowledgeGraph,
                  title: 'Configuration',
                  icon: BuildIcon,
                  tooltip: 'Edit general information about the knowledge graph and configure its database connection'
                },
                {
                  href: paths.import,
                  knowledgeGraph,
                  title: 'Select Data',
                  disabled: !kgPermissions.includes(KgPermissions.DatabaseSchemaImport),
                  icon: StorageIcon,
                  tooltip:
                    'Select additional tables and/or columns from your database to import into the knowledge graph'
                },
                {
                  href: paths.synchronization,
                  knowledgeGraph,
                  title: 'Synchronization',
                  disabled: !kgPermissions.includes(KgPermissions.KgSync),
                  icon: SyncIcon,
                  tooltip: 'Schedule or run synchronizations and view the results of past synchronizations'
                },
                user?.organizationUsesTenants && {
                  href: paths.tenants,
                  knowledgeGraph,
                  title: 'Tenant Access',
                  icon: TenantsIcon,
                  tooltip:
                    "Make the knowledge graph available for tenants and manage / update the tenant's knowledge graphs"
                },
                {
                  href: paths.dbtCloudSync,
                  knowledgeGraph,
                  title: 'dbt Cloud Sync',
                  disabled: !kgPermissions.includes(KgPermissions.Write),
                  icon: DbtIcon,
                  tooltip: "Integrate dbt with Veezoo to synchronize concept descriptions from dbt's semantic layer"
                },
                {
                  href: paths.queryEditor,
                  knowledgeGraph,
                  title: 'Query Editor',
                  disabled: !kgPermissions.includes(KgPermissions.DatabaseQuery),
                  icon: ManagedSearchIcon,
                  tooltip: 'Run SQL or VQL queries directly against the database and investigate VQL rewriting'
                }
              ])}
            />
            <div className={classes.editorMenuContainer} ref={editorMenuContainerRef} />
          </>
        )}
      </ResizableDrawer>
      {/* we need to wait for the knowledge graph to be set (or undefined) because some redirects depend on it */}
      {knowledgeGraph !== null && (
        <>
          <Switch>
            {/* when coming from Fivetran, extract the KG ID from the path and redirect to the configuration page */}
            <Redirect exact from={paths.configurationFivetran} to={`${paths.configuration}?id=:kgId`} />
            {/* if a knowledge graph is set, redirect from the root to the configuration page */}
            {knowledgeGraph && <Redirect exact from={paths.root} to={paths.configuration + location.search} />}
            <Route exact path={paths.root}>
              <Home knowledgeGraphs={knowledgeGraphs} fetchKnowledgeGraphs={getKnowledgeGraphs} />
            </Route>
            <Route exact path={paths.new}>
              <NewKnowledgeGraph
                languages={languages}
                orgSettings={orgSettings}
                fetchKnowledgeGraphs={getKnowledgeGraphs}
                editorRef={editorRef}
              />
            </Route>
            {/* if no knowledge graph is set, redirect to the root page */}
            {!knowledgeGraph && <Redirect to={paths.root} />}
            <Route exact path={paths.configuration}>
              <Configuration
                languages={languages}
                knowledgeGraph={knowledgeGraph}
                orgSettings={orgSettings}
                fetchKnowledgeGraphs={getKnowledgeGraphs}
                user={user}
              />
            </Route>
            <Route exact path={paths.dbtCloudSync}>
              <DbtSync knowledgeGraph={knowledgeGraph} />
            </Route>
            <Route exact path={paths.tenants}>
              <TenantAccess knowledgeGraph={knowledgeGraph} />
            </Route>
            <Route exact path={paths.import}>
              <Import
                knowledgeGraph={knowledgeGraph}
                onResultDialogClosed={() => setRunningBackgroundTask(knowledgeGraph, true)}
                editorRef={editorRef}
              />
            </Route>
            <Route exact path={paths.queryEditor}>
              <QueryEditor knowledgeGraph={knowledgeGraph} />
            </Route>
            <Route exact path={paths.synchronization}>
              <Synchronization knowledgeGraph={knowledgeGraph} editorRef={editorRef} />
            </Route>
            {/* match the Editor path (but don't render anything) to avoid falling back to the below redirect */}
            <Route exact path={paths.editor} />
            {/* redirect to the root page for all other paths */}
            <Redirect to={paths.root + location.search} />
          </Switch>
          {/* always render the Editor (hidden or shown) s.t. it renders its menu in the container through a portal */}
          <Editor
            ref={editorRef}
            style={{ display: showEditor ? 'block' : 'none' }}
            knowledgeGraph={knowledgeGraph}
            fetchKnowledgeGraphs={getKnowledgeGraphs}
            menuContainerRef={editorMenuContainerRef}
            user={user}
            setIsCommandPaletteOpen={setIsCommandPaletteOpen}
          />
          {/*Use a command palette to search and jump to the file or concept*/}
          {/*Each search result corresponds to one action, and selecting the action will jump to the result*/}
          <CommandPalette
            placeholderText="Search for definitions or files..."
            fetchActions={searchFilesAndConcepts}
            onExecuteAction={jumpToSearchResult}
            isOpen={isCommandPaletteOpen}
            onClose={() => setIsCommandPaletteOpen(false)}
          />
        </>
      )}
    </>
  );

  return (
    <ThemeProvider theme={theme}>
      <ContextMenuContextProvider>
        <ConfirmationModalProvider>
          {children}
          {isLoading ? <Progress /> : content}
        </ConfirmationModalProvider>
      </ContextMenuContextProvider>
    </ThemeProvider>
  );
};

export default Studio;
